/**
 * Budget grouping by category (epic #1960), shared by the Budgets page and the
 * Dashboard's budget groups (#1971) so a category has the same members, order
 * and Default rule on both.
 */

// Alphabetical, ignoring case: budgets inside a card and the cards themselves
// (epic #1960 decision 4). Order is never stored.
export const byName = (a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });

/**
 * A budget's category id, or null for Default. A budgetCategoryId that matches
 * no listed category files under Default (#1963 hand-off: treat a dangling
 * link defensively).
 * @param {Array<{budgetCategoryId: string}>} budgetCategories
 * @returns {function(object): (string|null)}
 */
export const makeCategoryIdOf = (budgetCategories) => {
  const known = new Set((budgetCategories || []).map((c) => c.budgetCategoryId));
  return (budget) => (known.has(budget.budgetCategoryId) ? budget.budgetCategoryId : null);
};

/**
 * One card per category plus Default, all sorted by name, members sorted by
 * name. Default shows only when it holds budgets or no categories exist yet
 * (epic #1960 decision 5). Pass Normal (type 0) budgets only: Pending Income
 * and Planned Expenses keep their own sections.
 * @param {Array} normalBudgets
 * @param {Array} budgetCategories
 * @param {string} defaultLabel  the Default card's name
 * @returns {Array<{key: string, name: string, budgetCategory: (object|null), members: Array}>}
 */
export const groupBudgetsByCategory = (normalBudgets, budgetCategories, defaultLabel) => {
  const categoryIdOf = makeCategoryIdOf(budgetCategories);
  const categories = budgetCategories || [];
  const defaultMembers = normalBudgets.filter((b) => categoryIdOf(b) === null).sort(byName);
  const cards = categories.map((c) => ({
    key: c.budgetCategoryId,
    name: c.name,
    budgetCategory: c,
    members: normalBudgets.filter((b) => b.budgetCategoryId === c.budgetCategoryId).sort(byName),
  }));
  if (defaultMembers.length > 0 || categories.length === 0) {
    cards.push({ key: 'default', name: defaultLabel, budgetCategory: null, members: defaultMembers });
  }
  return cards.sort(byName);
};

/**
 * A category's acronym (#2116): the first letter or digit of each word, a
 * hyphen starting a new word, so "Guilt-Free Spending" reads GFS. Computed
 * from the name on every render; nothing stores it.
 * @param {string} name
 * @returns {string}
 */
export const acronymOf = (name) => (name || '')
  .split(/[\s-]+/)
  .map((word) => (word.match(/[\p{L}\p{N}]/u) || [''])[0])
  .join('')
  .toUpperCase();

// Default's acronym is fixed text, not derived from its label (owner, #2116).
export const DEFAULT_ACRONYM = 'Def';

/**
 * The acronym a budget carries in pickers (#2116): Pending Income and Planned
 * Expenses by type, a Normal budget by its category, Def without one. A
 * workspace with no categories shows none at all.
 * @param {Array<{budgetCategoryId: string, name: string}>} budgetCategories
 * @returns {function(object): (string|null)}
 */
export const makeBudgetAcronymOf = (budgetCategories) => {
  const nameOf = new Map((budgetCategories || []).map((c) => [c.budgetCategoryId, c.name]));
  return (budget) => {
    if (nameOf.size === 0) return null;
    if (budget.type === 3) return acronymOf('Pending Income');
    if (budget.type === 2) return acronymOf('Planned Expenses');
    return nameOf.has(budget.budgetCategoryId) ? acronymOf(nameOf.get(budget.budgetCategoryId)) : DEFAULT_ACRONYM;
  };
};

// "Car (EE)", or the label alone when there is no acronym (#2116).
export const withAcronym = (label, acronym) => (acronym ? `${label} (${acronym})` : label);
